/**
 * ReceiptsSection.jsx - Tracking > Log time & expenses > Receipts: expenses
 * that are not a per-day amount (a flight change, tools, a taxi), typed one
 * by one - date, person, category, amount, billable or not, a receipt filed
 * in the project's Doc Hub (Cost Grids), notes. Billable ones are sold at
 * cost plus the handling fee like the per-day expenses; non-billable ones are
 * GLOBAL's own cost. Projects Edit adds, flips and removes them.
 */
import { useState } from 'react';
import { Btn, Field, GREEN, MUTED, NumInput, Section, SelectInput, TextInput, td, th } from '../quotes/quoteUi';
import { addExpense, deleteExpense, money, saveExpense, uploadReceipt } from '../costs/costApi';
import { previewAttachment } from '../../utils/attachmentPreview';
import { Empty, Hint, Num } from './trackingUi';
import { shortDate } from './trackingInsights';

const blank = (person = '') => ({ date: '', person, category: '', amount: '', billable: true, notes: '' });

export default function ReceiptsSection({ data, projectId, editable, reload, defaultPerson }) {
  const rows = data.expenses || [];
  const cats = data.categories || [];
  const people = data.people || [];
  const [row, setRow] = useState(() => blank(defaultPerson));
  const [file, setFile] = useState(null);
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const fail = (r, text) => {
    setMsg({ kind: 'error', text: (r.body && r.body.error) || text });
    setBusy(false);
  };

  const add = async () => {
    if (!row.date || !row.category || !(Number(row.amount) > 0)) {
      setMsg({ kind: 'error', text: 'A receipt needs its date, category and amount.' });
      return null;
    }
    setBusy(true);
    let receiptId = null;
    if (file) {
      const up = await uploadReceipt(projectId, file);
      if (!up.ok) return fail(up, 'Could not upload the receipt.');
      receiptId = up.id;
    }
    const r = await addExpense(projectId, { ...row, receiptId });
    if (!r.ok) return fail(r, 'Could not add the expense.');
    setRow(blank(row.person));
    setFile(null);
    setBusy(false);
    setMsg({ kind: 'ok', text: `Receipt added${receiptId ? ', filed in the Doc Hub (Cost Grids)' : ''}.` });
    return reload();
  };
  const toggle = async (e) => {
    const r = await saveExpense(projectId, e.id, { ...e, billable: !e.billable });
    if (!r.ok) return fail(r, 'Could not change the expense.');
    return reload();
  };
  const remove = async (e) => {
    if (!window.confirm(`Remove the ${e.category} expense of ${money(e.amount)} on ${e.date}? Its receipt stays in the Doc Hub.`))
      return null;
    const r = await deleteExpense(projectId, e.id);
    if (!r.ok) return fail(r, 'Could not remove the expense.');
    return reload();
  };

  return (
    <Section
      title={
        <>
          Receipts ({rows.length}){' '}
          <Hint
            title="Receipts"
            tip="An expense that is not one of the per-day amounts. Billable receipts are billed at cost plus the handling fee; non-billable ones are GLOBAL's own cost (they lower the margin, not the As sold)."
            from="typed here; the file goes to the Doc Hub > Cost Grids"
          />
        </>
      }
      hint="Expenses that are not a per-day amount: a flight change, tools, a one-off taxi."
      right={
        editable && (
          <Btn small primary={!open} onClick={() => setOpen((v) => !v)}>
            {open ? 'Close' : '+ Add a receipt'}
          </Btn>
        )
      }
    >
      {msg && (
        <div
          role={msg.kind === 'error' ? 'alert' : 'status'}
          style={{ marginBottom: 8, fontSize: 12, fontWeight: 600, color: msg.kind === 'error' ? '#B71C1C' : GREEN }}
        >
          {msg.text}
        </div>
      )}
      {editable && open && (
        <div
          style={{
            background: '#F6F8F3',
            borderRadius: 8,
            padding: 10,
            marginBottom: 10,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: 10,
          }}
        >
          <Field label="Date">
            <TextInput type="date" aria-label="Expense date" value={row.date} onChange={(v) => setRow((r) => ({ ...r, date: v }))} />
          </Field>
          <Field label="Person">
            <TextInput
              aria-label="Expense person"
              list="expense-people"
              value={row.person}
              onChange={(v) => setRow((r) => ({ ...r, person: v }))}
            />
            <datalist id="expense-people">
              {people.map((p) => (
                <option key={p.name} value={p.name} />
              ))}
            </datalist>
          </Field>
          <Field label="Category">
            <SelectInput
              aria-label="Expense category"
              value={row.category}
              blank="Pick one..."
              options={cats}
              onChange={(v) => setRow((r) => ({ ...r, category: v }))}
            />
          </Field>
          <Field label="Amount ($)">
            <NumInput aria-label="Expense amount" value={row.amount} onChange={(v) => setRow((r) => ({ ...r, amount: v }))} />
          </Field>
          <Field label="Billable?">
            <SelectInput
              aria-label="Expense billable"
              value={row.billable ? 'yes' : 'no'}
              options={[
                { value: 'yes', label: 'Billable' },
                { value: 'no', label: 'Non-billable' },
              ]}
              onChange={(v) => setRow((r) => ({ ...r, billable: v === 'yes' }))}
            />
          </Field>
          <Field label="Receipt file (optional)">
            <input
              type="file"
              aria-label="Expense receipt"
              onChange={(e) => setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
              style={{ fontSize: 11 }}
            />
          </Field>
          <Field label="Notes">
            <TextInput aria-label="Expense notes" value={row.notes} onChange={(v) => setRow((r) => ({ ...r, notes: v }))} />
          </Field>
          <div style={{ alignSelf: 'end' }}>
            <Btn primary disabled={busy} onClick={add}>
              {busy ? 'Adding...' : 'Add expense'}
            </Btn>
          </div>
        </div>
      )}
      {!rows.length ? (
        <Empty text="No receipts. Expense days add each person's per-day amounts by themselves - add a receipt only for something else." />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table aria-label="Expenses" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Date', 'Person', 'Category', 'Amount', 'Billable', 'Receipt', 'Notes', ''].map((x) => (
                  <th key={x} style={th}>
                    {x}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id}>
                  <td style={{ ...td, fontSize: 11.5, whiteSpace: 'nowrap' }}>{shortDate(e.date, true)}</td>
                  <td style={{ ...td, fontSize: 11.5 }}>{e.person}</td>
                  <td style={{ ...td, fontSize: 11.5 }}>{e.category}</td>
                  <Num v={money(e.amount)} strong />
                  <td style={td}>
                    <button
                      type="button"
                      disabled={!editable}
                      onClick={() => toggle(e)}
                      aria-label={`${e.category} ${e.date} billable`}
                      style={{
                        fontSize: 10.5,
                        fontWeight: 700,
                        padding: '2px 8px',
                        borderRadius: 10,
                        border: '1px solid #D6DCCB',
                        cursor: editable ? 'pointer' : 'default',
                        background: e.billable ? '#E8F5E9' : '#FFF3E0',
                        fontFamily: 'inherit',
                      }}
                    >
                      {e.billable ? 'Billable' : 'Non-billable'}
                    </button>
                  </td>
                  <td style={{ ...td, fontSize: 11.5 }}>
                    {e.receiptId ? (
                      <Btn small onClick={() => previewAttachment(e.receiptId)}>
                        View
                      </Btn>
                    ) : (
                      <span style={{ color: MUTED }}>-</span>
                    )}
                  </td>
                  <td style={{ ...td, fontSize: 11.5 }}>{e.notes}</td>
                  <td style={{ ...td, width: 30 }}>
                    {editable && (
                      <Btn small danger aria-label={`Remove ${e.category} ${e.date}`} onClick={() => remove(e)}>
                        x
                      </Btn>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}
